import { useEffect, useState, useCallback } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { appConfirm } from "../../../components/ConfirmDialog";
import { Loader2, Plus, Trash2, Send, X, HardHat } from "lucide-react";

const TYPES = [
  { key: "update", label: "Site update", cls: "bg-[#DCE9F5] text-[#12263A]" },
  { key: "certificate", label: "Certificate", cls: "bg-[#D8EED8] text-[#1F6B3B]" },
  { key: "paperwork", label: "Paperwork", cls: "bg-[#F0EAE0] text-[#6B7280]" },
];
const typeDef = (k) => TYPES.find(t => t.key === k) || TYPES[0];

export function SiteLog({ reference, clientEmail, clientName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("update");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [composer, setComposer] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/booking/${reference}/site-log`);
      setItems(r.data.items || []);
    } catch { toast.error("Could not load the site log"); }
    setLoading(false);
  }, [reference]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!body.trim()) { toast.error("Type the entry first"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/booking/${reference}/site-log`, { type, body: body.trim() });
      setBody("");
      toast.success("Logged");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const del = async (entry) => {
    if (!(await appConfirm({ title: "Delete this entry?", description: entry.body.slice(0, 140), confirmLabel: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/admin/booking/${reference}/site-log/${entry.id}`);
      setItems(list => list.filter(i => i.id !== entry.id));
      setSelected(s => s.filter(id => id !== entry.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const openComposer = () => {
    const chosen = items.filter(i => selected.includes(i.id));
    const lines = chosen.map(i => `• ${i.body}`).join("\n\n");
    const first = (clientName || "").split(" ")[0];
    setComposer({
      entryIds: [...selected],
      subject: `Progress update on your extension — ${reference}`,
      body: `Hi${first ? ` ${first}` : ""},\n\nA quick update from site on your project (${reference}):\n\n${lines}\n\nAny questions at all, just reply to this email.\n\nBest regards,\nThe Extension Works`,
    });
  };

  return (
    <div data-testid="jobfile-sitelog">
      <div className="border border-[#E5E7EB] bg-white p-4 mb-4">
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><HardHat size={12} /> New entry (office use — nothing is sent automatically)</div>
        <div className="flex gap-2 flex-wrap mb-2">
          {TYPES.map(t => (
            <button key={t.key} data-testid={`sitelog-type-${t.key}`} onClick={() => setType(t.key)}
              className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest border ${type === t.key ? "border-[#12263A] " + t.cls : "border-[#E5E7EB] text-[#9CA3AF] bg-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <textarea data-testid="sitelog-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3}
          placeholder="e.g. Footings poured and inspected by Building Control — passed. Electrical first fix booked for Monday…"
          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm resize-y focus:border-[#C79A3B] outline-none" />
        <div className="flex justify-end mt-2">
          <button data-testid="sitelog-add" onClick={add} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B] disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add to site log
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="label-eyebrow">Log ({items.length})</div>
        <button data-testid="sitelog-send-update" onClick={openComposer} disabled={selected.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#1F6B3B] text-white hover:bg-[#155029] disabled:opacity-40">
          <Send size={12} /> Send update to client ({selected.length} selected)
        </button>
      </div>

      {loading && <div className="py-4 text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-[#E5E7EB] bg-[#F8F6F2] p-5 text-center text-sm text-[#6B7280]" data-testid="sitelog-empty">
          Nothing logged yet — record site updates, certificates and paperwork here, then send the client a tidy update in one go.
        </div>
      )}
      <div className="space-y-2">
        {items.map((e, i) => {
          const t = typeDef(e.type);
          return (
            <div key={e.id} data-testid={`sitelog-entry-${i}`} className="border border-[#F0EAE0] bg-white p-3 flex items-start gap-3">
              <input type="checkbox" data-testid={`sitelog-select-${i}`} checked={selected.includes(e.id)}
                onChange={() => toggle(e.id)} className="mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full ${t.cls}`}>{t.label}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{new Date(e.created_at).toLocaleString("en-GB")}{e.author_name || e.author ? ` · ${e.author_name || e.author}` : ""}</span>
                  {e.sent_to_client_at && (
                    <span data-testid={`sitelog-sent-${i}`} className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full bg-[#D8EED8] text-[#1F6B3B]">
                      Sent to client {new Date(e.sent_to_client_at).toLocaleDateString("en-GB")}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#374151] whitespace-pre-wrap">{e.body}</p>
              </div>
              <button data-testid={`sitelog-del-${i}`} onClick={() => del(e)} className="text-[#9CA3AF] hover:text-[#C0392B] p-1 shrink-0"><Trash2 size={14} /></button>
            </div>
          );
        })}
      </div>

      {composer && (
        <UpdateComposer reference={reference} clientEmail={clientEmail} composer={composer} setComposer={setComposer}
          entryIds={composer.entryIds} onSent={() => { setSelected([]); setComposer(null); load(); }} />
      )}
    </div>
  );
}

function UpdateComposer({ reference, clientEmail, composer, setComposer, entryIds, onSent }) {
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/admin/booking/${reference}/site-log/send-update`, {
        subject: composer.subject, body: composer.body, entry_ids: entryIds,
      });
      toast.success("Update sent to the client — logged in Comms");
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the update"); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => !sending && setComposer(null)}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="sitelog-composer">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold tracking-tight">Send update to client</h3>
          <button data-testid="sitelog-composer-close" onClick={() => !sending && setComposer(null)} className="text-[#6B7280] hover:text-[#C79A3B]"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#6B7280] mb-4">To <b>{clientEmail || "no email on file"}</b> via your connected Gmail — it will be logged on the Comms tab and the included entries stamped as sent.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-1">Subject</label>
            <input data-testid="sitelog-composer-subject" value={composer.subject}
              onChange={(e) => setComposer(c => ({ ...c, subject: e.target.value }))}
              className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-3" />
            <label className="label-eyebrow block mb-1">Message (edit freely)</label>
            <textarea data-testid="sitelog-composer-body" value={composer.body} rows={14}
              onChange={(e) => setComposer(c => ({ ...c, body: e.target.value }))}
              className="w-full border border-[#E5E7EB] px-3 py-2 text-sm resize-y" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Preview</label>
            <div className="border border-[#E5E7EB] bg-[#F8F6F2] p-4" data-testid="sitelog-composer-preview">
              <div className="text-sm font-bold text-[#12263A] border-b border-[#E5E7EB] pb-2 mb-3">{composer.subject || "(no subject)"}</div>
              <div className="text-sm text-[#374151] whitespace-pre-wrap">{composer.body}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={() => setComposer(null)} disabled={sending} className="de-btn-secondary text-sm">Cancel</button>
          <button data-testid="sitelog-composer-send" onClick={send} disabled={sending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[#1F6B3B] text-white hover:bg-[#155029] disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send update
          </button>
        </div>
      </div>
    </div>
  );
}
